import { 
  Box, 
  Typography, 
  Container, 
  Card, 
  CardMedia, 
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Link,
  IconButton,
  Stack,
  Button,
  TextField,
  Pagination
} from '@mui/material';
import { useState, useEffect } from 'react';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import EmailIcon from '@mui/icons-material/Email';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../utils/roles';
import { useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { authService } from '../../services/authService';

function Etudiants() {
  const [etudiants, setEtudiants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const etudiantsParPage = 9;
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const fetchEtudiants = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la récupération des étudiants');
        }

        const data = await response.json();
        const etudiantsData = data.etudiants.map(etudiant => ({
          id: etudiant.id_etudiants,
          nom: etudiant.nom,
          prenom: etudiant.prenom,
          email: etudiant.email,
          formation: etudiant.formation || '',
          annee: etudiant.annee || '',
          description: etudiant.description || '',
          specialite: etudiant.specialite || '',
          github: etudiant.github || '',
          linkedin: etudiant.linkedin || '',
          photo: etudiant.photo || '',
          competences: etudiant.etudiant?.competences?.map(comp => comp.nom_comp) || []
        }));
        
        setEtudiants(etudiantsData);
        setLoading(false);
      } catch (err) {
        console.error("Erreur lors du chargement des étudiants:", err);
        setError("Impossible de charger les étudiants");
        setLoading(false);
      }
    };

    fetchEtudiants();
  }, []);

  useEffect(() => {
    // Récupérer le numéro de page depuis l'URL
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page')) || 1;
    setPage(pageParam);
  }, [location.search]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredEtudiants = etudiants.filter(etudiant => 
    etudiant.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    etudiant.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    etudiant.competences.some(comp => 
      comp.toLowerCase().includes(searchTerm.toLowerCase())
    ) ||
    etudiant.formation.toLowerCase().includes(searchTerm.toLowerCase()) ||
    etudiant.specialite.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const indexDernierElement = page * etudiantsParPage;
  const indexPremierElement = indexDernierElement - etudiantsParPage;
  const etudiantsAffiches = filteredEtudiants.slice(indexPremierElement, indexDernierElement);
  const nombrePages = Math.ceil(filteredEtudiants.length / etudiantsParPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Mettre à jour l'URL
    navigate(`/etudiants?page=${newPage}`);
    window.scrollTo({
      top: document.getElementById('etudiants-list').offsetTop - 20,
      behavior: 'smooth'
    });
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress color="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography color="error" align="center">
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography 
          variant="h2" 
          component="h1" 
          align="center" 
          gutterBottom
          sx={{
            opacity: 0,
            animation: 'fadeIn 0.8s ease-out forwards',
            '@keyframes fadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 }
            }
          }}
        >
          Nos Étudiants
        </Typography>

        <Box sx={{ 
          display: 'flex', 
          gap: 2,
          mb: 4,
          opacity: 0,
          animation: 'fadeIn 0.8s ease-out 0.3s forwards',
          '@keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 }
          }
        }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Rechercher par nom, compétences, formation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              bgcolor: 'background.paper',
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
          
          {(userRole === ROLES.ADMIN || userRole === ROLES.PILOTE) && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/etudiants/new')}
              sx={{
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: 'secondary.main' }
              }}
            >
              Ajouter un étudiant
            </Button>
          )}
        </Box>
        
        <Box id="etudiants-list">
          <Grid 
            container 
            spacing={4} 
            sx={{ mt: 2 }}
            justifyContent="center"
          >
            {etudiantsAffiches.map((etudiant, index) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                key={etudiant.id}
                sx={{ 
                  maxWidth: '400px',
                  ...(index < 3 ? {
                    opacity: 0,
                    animation: `fadeIn 0.8s ease-out ${0.6 + index * 0.2}s forwards`,
                    '@keyframes fadeIn': {
                      '0%': { opacity: 0, transform: 'translateY(30px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' }
                    }
                  } : {
                    opacity: scrollY > (150 + (index - 3) * 50) ? 1 : 0,
                    transform: `translateY(${scrollY > (150 + (index - 3) * 50) ? 0 : 30}px)`,
                    transition: 'all 0.6s ease-out',
                  })
                }}
              >
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    position: 'relative',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    }
                  }}
                  onClick={() => navigate(`/etudiants/${etudiant.id}/suivi`)}
                >
                  {(userRole === ROLES.ADMIN || userRole === ROLES.PILOTE) && (
                    <IconButton
                      className="edit-button"
                      onClick={(e) => {
                        e.stopPropagation(); // Empêche la navigation vers le suivi
                        navigate(`/etudiants/${etudiant.id}/edit`);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'background.paper',
                        zIndex: 1,
                        '&:hover': {
                          bgcolor: 'primary.main'
                        }
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  )}

                  <CardMedia
                    component="img"
                    height="300"
                    image={etudiant.photo}
                    alt={`${etudiant.prenom} ${etudiant.nom}`}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" gutterBottom>
                      {etudiant.prenom} {etudiant.nom}
                    </Typography>
                    
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      {etudiant.formation} - {etudiant.annee}
                    </Typography>
                    
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Spécialité : {etudiant.specialite}
                    </Typography>

                    <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                      {etudiant.description}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Compétences :
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                        {etudiant.competences.map((competence, index) => (
                          <Chip 
                            key={index}
                            label={competence}
                            size="small"
                            color="primary"
                          />
                        ))}
                      </Stack>
                    </Box>

                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      gap: 2,
                      mt: 2 
                    }}>
                      <IconButton 
                        component={Link}
                        href={etudiant.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: 'primary.main',
                          '&:hover': { color: 'primary.dark' }
                        }}
                      >
                        <LinkedInIcon />
                      </IconButton>
                      <IconButton 
                        component={Link}
                        href={etudiant.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: 'primary.main',
                          '&:hover': { color: 'primary.dark' }
                        }}
                      >
                        <GitHubIcon />
                      </IconButton>
                      <IconButton 
                        component={Link}
                        href={`mailto:${etudiant.email}`}
                        sx={{ 
                          color: 'primary.main',
                          '&:hover': { color: 'primary.dark' }
                        }}
                      >
                        <EmailIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination avec animation */}
          {nombrePages > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
                mb: 2,
                opacity: scrollY > 200 ? 1 : 0,
                transform: `translateY(${scrollY > 200 ? 0 : 30}px)`,
                transition: 'all 0.6s ease-out',
              }}
            >
              <Pagination 
                count={nombrePages}
                page={page}
                onChange={handleChangePage}
                color="primary"
                size="large"
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: 'text.primary',
                  },
                  '& .Mui-selected': {
                    bgcolor: 'primary.main !important',
                    color: 'primary.contrastText !important'
                  }
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
}

export default Etudiants;